// Ephemeral `presence` — per-chat peer state + outbound online/offline.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { ChatPresence } from '../chat/ChatRoom.types';
import type { EphemeralClient } from './EphemeralClient';
import { conversationIdEqual } from './convMatch';
import { deviceKeyFromBytes } from './typingFormat';
import type { EphemeralEvent } from './types';
import {
  aggregatePeerPresence,
  parsePresencePayload,
  presenceToChatPresence,
  type PeerPresenceEntry,
} from './presenceFormat';
import type { PresenceState } from './types';
import { log } from '../log/log';

export interface UseEphemeralPresenceOptions {
  readonly conversationId: string;
  readonly conversationIdBytes: Uint8Array;
  readonly getClient: () => EphemeralClient | null;
  readonly enabled?: boolean;
}

export interface UseEphemeralPresenceResult {
  readonly peerPresence: ChatPresence;
}

export function useEphemeralPresence(
  options: UseEphemeralPresenceOptions,
): UseEphemeralPresenceResult {
  const { conversationId, conversationIdBytes, getClient, enabled = true } =
    options;

  // A caller that decodes `conversationIdBytes` fresh every render would give
  // the effect a new Uint8Array reference each time, tearing down and
  // re-subscribing the WS (unsubscribe + offline publish storm) on every
  // render. Derive a stable hex key and latch the bytes identity to it so the
  // reference only changes when the conversation actually changes.
  const conversationKey = deviceKeyFromBytes(conversationIdBytes);
  // biome-ignore lint/correctness/useExhaustiveDependencies: latch to conversationKey by design
  const conversationBytes = useMemo(() => conversationIdBytes, [conversationKey]);

  const peersRef = useRef<Map<string, PeerPresenceEntry>>(new Map());
  const [peerPresence, setPeerPresence] = useState<ChatPresence>(null);

  const syncPresence = useCallback(() => {
    setPeerPresence(aggregatePeerPresence([...peersRef.current.values()]));
  }, []);

  const publishPresence = useCallback(
    (state: PresenceState) => {
      const client = getClient();
      if (client == null) return;
      void client
        .publishPresence(conversationBytes, state)
        .catch(() => log.debug('ephemeral.presence.publish_failed'));
    },
    [conversationBytes, getClient],
  );

  const applyEvent = useCallback(
    (event: EphemeralEvent) => {
      if (event.type !== 'presence') return;
      const wire = parsePresencePayload(event.payload);
      if (wire === null) return;
      const key = deviceKeyFromBytes(event.senderDevice);
      peersRef.current.set(key, {
        deviceKey: key,
        presence: presenceToChatPresence(wire),
        updatedAtMs: Date.now(),
      });
      syncPresence();
    },
    [syncPresence],
  );

  useEffect(() => {
    if (!enabled) return undefined;
    type Client = NonNullable<ReturnType<typeof getClient>>;
    let teardown: (() => void) | null = null;
    let pollTimer: ReturnType<typeof setInterval> | null = null;

    const subscribe = (client: Client): void => {
      client.subscribe([conversationBytes]);

      const publishVisibleState = (): void => {
        if (typeof document !== 'undefined' && document.hidden) {
          publishPresence('away');
        } else {
          publishPresence('online');
        }
      };

      publishVisibleState();

      const unsubEvent = client.onEvent((event) => {
        if (!conversationIdEqual(event.conversationId, conversationId)) {
          return;
        }
        applyEvent(event);
      });

      const pruneTimer = setInterval(() => {
        syncPresence();
      }, 5_000);

      const onVisibility = (): void => {
        publishVisibleState();
      };
      if (typeof document !== 'undefined') {
        document.addEventListener('visibilitychange', onVisibility);
      }

      teardown = () => {
        unsubEvent();
        client.unsubscribe([conversationBytes]);
        clearInterval(pruneTimer);
        if (typeof document !== 'undefined') {
          document.removeEventListener('visibilitychange', onVisibility);
        }
        publishPresence('offline');
        peersRef.current.clear();
        setPeerPresence(null);
      };
    };

    const client = getClient();
    if (client != null) {
      subscribe(client);
    } else {
      // WS client not ready right after a reload — poll until it
      // connects, then subscribe (mirrors useEphemeralTyping). Without
      // this, presence never appears post-reload.
      pollTimer = setInterval(() => {
        const c = getClient();
        if (c != null) {
          if (pollTimer != null) {
            clearInterval(pollTimer);
            pollTimer = null;
          }
          subscribe(c);
        }
      }, 500);
    }

    return () => {
      if (pollTimer != null) clearInterval(pollTimer);
      teardown?.();
    };
  }, [
    enabled,
    conversationId,
    conversationBytes,
    getClient,
    applyEvent,
    syncPresence,
    publishPresence,
  ]);

  return { peerPresence };
}
